import { Head, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { useState } from 'react'
import { FileText, Send, CheckCircle, Clock, Eye, AlertCircle, RefreshCw, Settings, Loader2, X, FileCheck, Mail } from 'lucide-react'

interface Template {
  id: number
  name: string
  created_at: string
  fields_count: number
  folder_name: string | null
}

interface Submitter {
  id: number
  email: string
  name: string
  status: string
  completed_at: string | null
  opened_at: string | null
  sent_at: string | null
}

interface Submission {
  id: number
  template_id: number
  created_at: string
  status: string
  submitters: Submitter[]
}

interface Document {
  id: number
  document_name: string
  document_type: string
  status: string
  docuseal_template_id: string | null
  docuseal_submission_id: string | null
  sent_at: string | null
  viewed_at: string | null
  signed_at: string | null
  signed_document_url: string | null
  signed_documents: Array<{ name: string; url: string }> | null
  created_at: string
}

interface AgentWithDocuments {
  id: number
  name: string
  email: string | null
  onboarding_status: string
  documents: Document[]
}

interface AgentWithoutContract {
  id: number
  name: string
  email: string | null
  status: string
}

interface BulkSendResult {
  id: number
  name: string
  email: string | null
  success: boolean
  skipped?: boolean
  error: string | null
}

interface Props extends PageProps {
  docuseal_configured: boolean
  templates: Template[]
  templates_error?: string
  recent_submissions: Submission[]
  pending_documents: AgentWithDocuments[]
  signed_documents: AgentWithDocuments[]
  agents_without_contract: AgentWithoutContract[]
  bulk_send_results: BulkSendResult[] | null
  default_template_id: string | null
}

type TabType = 'send' | 'pending' | 'signed' | 'templates'

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Mail },
  viewed: { label: 'Viewed', color: 'bg-amber-100 text-amber-700', icon: Eye },
  signed: { label: 'Signed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: AlertCircle }
}

export default function DocumentsIndex({
  docuseal_configured,
  templates,
  templates_error,
  pending_documents,
  signed_documents,
  agents_without_contract,
  bulk_send_results,
  default_template_id
}: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('send')
  const [showBulkResults, setShowBulkResults] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState(default_template_id || '')
  const [selectedAgents, setSelectedAgents] = useState<number[]>([])
  const [customMessage, setCustomMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sendModalAgent, setSendModalAgent] = useState<AgentWithoutContract | null>(null)
  const [refreshingDoc, setRefreshingDoc] = useState<number | null>(null)

  const tabs = [
    { id: 'send' as TabType, label: 'Send Contracts', icon: Send, count: agents_without_contract.length },
    { id: 'pending' as TabType, label: 'Pending Signatures', icon: Clock, count: pending_documents.length },
    { id: 'signed' as TabType, label: 'Signed Documents', icon: CheckCircle, count: signed_documents.length },
    { id: 'templates' as TabType, label: 'Templates', icon: FileText, count: templates.length }
  ]

  const handleSendSingle = (agent: AgentWithoutContract) => {
    setSendModalAgent(agent)
  }

  const handleConfirmSend = () => {
    if (!sendModalAgent || !selectedTemplate) return
    setSending(true)
    router.post('/documents/send', {
      agent_id: sendModalAgent.id,
      template_id: selectedTemplate,
      message: customMessage
    }, {
      onFinish: () => {
        setSending(false)
        setSendModalAgent(null)
        setCustomMessage('')
      }
    })
  }

  const handleSendBulk = () => {
    if (selectedAgents.length === 0 || !selectedTemplate) return
    setSending(true)
    router.post('/documents/send_bulk', {
      agent_ids: selectedAgents,
      template_id: selectedTemplate
    }, {
      onFinish: () => {
        setSending(false)
        setSelectedAgents([])
      }
    })
  }

  const handleRefreshStatus = (docId: number) => {
    setRefreshingDoc(docId)
    router.post(`/documents/${docId}/refresh`, {}, {
      onFinish: () => setRefreshingDoc(null)
    })
  }

  const handleSetDefault = (templateId: number) => {
    router.post('/documents/set_default', { template_id: templateId })
  }

  const toggleAgent = (agentId: number) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    )
  }

  const selectAllAgents = () => {
    if (selectedAgents.length === agents_without_contract.length) {
      setSelectedAgents([])
    } else {
      setSelectedAgents(agents_without_contract.map(a => a.id))
    }
  }

  return (
    <AppLayout>
      <Head title="Document Management" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Document Management</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Send contracts and track e-signatures with DocuSeal</p>
        </div>

        {!docuseal_configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-amber-800">DocuSeal Not Configured</h3>
              <p className="text-sm text-amber-700 mt-1">
                Please configure DocuSeal in Settings &gt; Integrations to use e-signature features.
              </p>
              <a href="/settings/integrations" className="text-sm text-amber-800 underline mt-2 inline-block">
                Go to Integrations
              </a>
            </div>
          </div>
        )}

        {templates_error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">Error loading templates: {templates_error}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="border-b border-chestnut-charcoal/10">
            <nav className="flex -mb-px overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-chestnut-forest text-chestnut-forest'
                        : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        activeTab === tab.id ? 'bg-chestnut-forest/10' : 'bg-chestnut-charcoal/10'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'send' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-chestnut-charcoal">Send Contracts</h2>
                    <p className="text-sm text-chestnut-charcoal/60">
                      Send contracts to agents who haven't received one yet
                    </p>
                  </div>
                  {agents_without_contract.length > 0 && (
                    <div className="flex items-center gap-4">
                      <select
                        value={selectedTemplate}
                        onChange={e => setSelectedTemplate(e.target.value)}
                        className="input"
                        disabled={templates.length === 0}
                      >
                        <option value="">Select template...</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleSendBulk}
                        disabled={selectedAgents.length === 0 || !selectedTemplate || sending}
                        className="btn-primary flex items-center gap-2"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send to Selected ({selectedAgents.length})
                      </button>
                    </div>
                  )}
                </div>

                {bulk_send_results && bulk_send_results.length > 0 && showBulkResults && (
                  <div className="border border-chestnut-charcoal/10 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-chestnut-cream/50 flex items-center justify-between">
                      <p className="text-sm font-medium text-chestnut-charcoal">
                        Last bulk send: {bulk_send_results.filter(r => r.success).length} sent
                        {bulk_send_results.some(r => !r.success && !r.skipped) &&
                          `, ${bulk_send_results.filter(r => !r.success && !r.skipped).length} failed`}
                        {bulk_send_results.some(r => r.skipped) &&
                          `, ${bulk_send_results.filter(r => r.skipped).length} skipped`}
                      </p>
                      <button
                        onClick={() => setShowBulkResults(false)}
                        className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal rounded"
                        title="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="divide-y divide-chestnut-charcoal/5">
                      {bulk_send_results.map(result => (
                        <div key={result.id} className="px-4 py-2 flex items-start gap-3 bg-white">
                          {result.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          ) : result.skipped ? (
                            <FileCheck className="h-4 w-4 text-chestnut-charcoal/40 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm text-chestnut-charcoal">{result.name}</p>
                            {result.success ? (
                              <p className="text-xs text-chestnut-charcoal/60">Contract sent</p>
                            ) : result.skipped ? (
                              <p className="text-xs text-chestnut-charcoal/60 break-words">
                                Skipped — {result.error}
                              </p>
                            ) : (
                              <p className="text-xs text-red-600 break-words">{result.error}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {agents_without_contract.length === 0 ? (
                  <div className="text-center py-12 text-chestnut-charcoal/60">
                    <FileCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>All agents have been sent contracts</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 px-4 py-2 bg-chestnut-cream/50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedAgents.length === agents_without_contract.length}
                        onChange={selectAllAgents}
                        className="rounded border-chestnut-charcoal/30"
                      />
                      <span className="text-sm font-medium text-chestnut-charcoal">Select All</span>
                    </div>
                    {agents_without_contract.map(agent => (
                      <div
                        key={agent.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-chestnut-charcoal/10 bg-white hover:bg-chestnut-cream/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedAgents.includes(agent.id)}
                            onChange={() => toggleAgent(agent.id)}
                            className="rounded border-chestnut-charcoal/30"
                          />
                          <div>
                            <p className="font-medium text-chestnut-charcoal">{agent.name}</p>
                            <p className="text-sm text-chestnut-charcoal/60">{agent.email || 'No email'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">
                            {agent.status.replace('_', ' ')}
                          </span>
                          <button
                            onClick={() => handleSendSingle(agent)}
                            disabled={!docuseal_configured || templates.length === 0}
                            className="btn-secondary text-sm flex items-center gap-1"
                          >
                            <Send className="h-3.5 w-3.5" />
                            Send
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pending' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-chestnut-charcoal">Pending Signatures</h2>
                  <p className="text-sm text-chestnut-charcoal/60">
                    Documents waiting to be signed by agents
                  </p>
                </div>

                {pending_documents.length === 0 ? (
                  <div className="text-center py-12 text-chestnut-charcoal/60">
                    <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No documents pending signature</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pending_documents.map(agent => (
                      <div key={agent.id} className="border border-chestnut-charcoal/10 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-chestnut-cream/30 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-chestnut-charcoal">{agent.name}</p>
                            <p className="text-sm text-chestnut-charcoal/60">{agent.email}</p>
                          </div>
                        </div>
                        <div className="divide-y divide-chestnut-charcoal/5">
                          {agent.documents.map(doc => {
                            const status = statusConfig[doc.status] || statusConfig.pending
                            const StatusIcon = status.icon
                            return (
                              <div key={doc.id} className="px-4 py-3 flex items-center justify-between bg-white">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-chestnut-charcoal/40" />
                                  <div>
                                    <p className="font-medium text-chestnut-charcoal text-sm">{doc.document_name}</p>
                                    <p className="text-xs text-chestnut-charcoal/60">
                                      Sent {doc.sent_at ? new Date(doc.sent_at).toLocaleDateString() : '-'}
                                      {doc.viewed_at && ` • Viewed ${new Date(doc.viewed_at).toLocaleDateString()}`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${status.color}`}>
                                    <StatusIcon className="h-3 w-3" />
                                    {status.label}
                                  </span>
                                  <button
                                    onClick={() => handleRefreshStatus(doc.id)}
                                    disabled={refreshingDoc === doc.id}
                                    className="p-1.5 text-chestnut-charcoal/40 hover:text-chestnut-forest rounded-lg hover:bg-chestnut-cream/50"
                                    title="Refresh status"
                                  >
                                    <RefreshCw className={`h-4 w-4 ${refreshingDoc === doc.id ? 'animate-spin' : ''}`} />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'signed' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-chestnut-charcoal">Signed Documents</h2>
                  <p className="text-sm text-chestnut-charcoal/60">
                    Documents that have been signed by agents
                  </p>
                </div>

                {signed_documents.length === 0 ? (
                  <div className="text-center py-12 text-chestnut-charcoal/60">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No signed documents yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {signed_documents.map(agent => (
                      <div key={agent.id} className="border border-chestnut-charcoal/10 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-green-50 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-chestnut-charcoal">{agent.name}</p>
                            <p className="text-sm text-chestnut-charcoal/60">{agent.email}</p>
                          </div>
                        </div>
                        <div className="divide-y divide-chestnut-charcoal/5">
                          {agent.documents.filter(d => d.status === 'signed').map(doc => {
                            const files = (doc.signed_documents && doc.signed_documents.length > 0)
                              ? doc.signed_documents
                              : (doc.signed_document_url ? [{ name: 'View Document', url: doc.signed_document_url }] : [])
                            return (
                              <div key={doc.id} className="px-4 py-3 flex items-start justify-between bg-white gap-4">
                                <div className="flex items-center gap-3">
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                  <div>
                                    <p className="font-medium text-chestnut-charcoal text-sm">{doc.document_name}</p>
                                    <p className="text-xs text-chestnut-charcoal/60">
                                      Signed {doc.signed_at ? new Date(doc.signed_at).toLocaleDateString() : '-'}
                                    </p>
                                  </div>
                                </div>
                                {files.length > 0 && (
                                  <div className="flex flex-col items-end gap-2 shrink-0">
                                    {files.map((file, idx) => (
                                      <a
                                        key={idx}
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-secondary text-sm whitespace-nowrap"
                                      >
                                        {files.length > 1 ? `View: ${file.name}` : 'View Document'}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-chestnut-charcoal">DocuSeal Templates</h2>
                    <p className="text-sm text-chestnut-charcoal/60">
                      Templates from your DocuSeal account
                    </p>
                  </div>
                  <a
                    href="https://docuseal.co/templates"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Manage in DocuSeal
                  </a>
                </div>

                {templates.length === 0 ? (
                  <div className="text-center py-12 text-chestnut-charcoal/60">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No templates found</p>
                    <p className="text-sm mt-2">Create templates in your DocuSeal account</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates.map(template => (
                      <div
                        key={template.id}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          default_template_id === template.id.toString()
                            ? 'border-chestnut-forest bg-chestnut-forest/5'
                            : 'border-chestnut-charcoal/10 bg-white hover:border-chestnut-charcoal/20'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <FileText className="h-8 w-8 text-chestnut-forest/70" />
                          {default_template_id === template.id.toString() && (
                            <span className="text-xs px-2 py-0.5 bg-chestnut-forest text-white rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <h3 className="font-medium text-chestnut-charcoal mb-1">{template.name}</h3>
                        <p className="text-xs text-chestnut-charcoal/60 mb-3">
                          {template.fields_count} fields • Created {new Date(template.created_at).toLocaleDateString()}
                        </p>
                        {default_template_id !== template.id.toString() && (
                          <button
                            onClick={() => handleSetDefault(template.id)}
                            className="text-sm text-chestnut-forest hover:underline"
                          >
                            Set as default
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {sendModalAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-chestnut-charcoal/10">
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Send Contract</h3>
              <button onClick={() => setSendModalAgent(null)} className="p-1 text-chestnut-charcoal/40 hover:text-chestnut-charcoal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-chestnut-cream/50 rounded-lg p-3">
                <p className="font-medium text-chestnut-charcoal">{sendModalAgent.name}</p>
                <p className="text-sm text-chestnut-charcoal/60">{sendModalAgent.email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Template</label>
                <select
                  value={selectedTemplate}
                  onChange={e => setSelectedTemplate(e.target.value)}
                  className="input w-full"
                >
                  <option value="">Select template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Custom Message (optional)</label>
                <textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  className="input w-full"
                  rows={3}
                  placeholder="Add a personal message to include in the email..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setSendModalAgent(null)} className="btn-secondary">Cancel</button>
                <button
                  onClick={handleConfirmSend}
                  disabled={!selectedTemplate || sending}
                  className="btn-primary flex items-center gap-2"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send Contract
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
